import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import API_BASE_URL from '../../config/api';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { toast } from 'react-toastify';

export default function BoutiqueSchedule() {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthsData, setMonthsData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchYearSchedule();
  }, [currentYear]);

  const fetchYearSchedule = async () => {
    try {
      setLoading(true);
      const promises = months.map((_, index) =>
        fetch(`${API_BASE_URL}/api/schedule/working-days/${currentYear}/${index + 1}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }).then(res => res.json())
      );

      const results = await Promise.all(promises);
      const data = {};
      results.forEach((result, index) => {
        data[index + 1] = result;
      });
      setMonthsData(data);
    } catch (error) {
      console.error('Error fetching year schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousYear = () => {
    setCurrentYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setCurrentYear(prev => prev + 1);
  };

  const handleMonthClick = (monthIndex) => {
    navigate(`/boutique-schedule/${currentYear}/${monthIndex + 1}`);
  };

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          background: "linear-gradient(135deg, #1a1a1a 0%, #3d3d3d 100%)",
          p: { xs: 2, md: 4 },
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 500,
            color: "#c4a574",
            textDecoration: "underline",
            textDecorationColor: "#8B7355",
            textDecorationThickness: 2,
            textUnderlineOffset: 8,
          }}
        >
          BOUTIQUE SCHEDULE
        </Typography>

        {/* Year Navigation */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 4,
            gap: 2,
          }}
        >
          <IconButton
            onClick={handlePreviousYear}
            sx={{
              bgcolor: '#C4A574',
              color: '#000',
              '&:hover': { bgcolor: '#B39562' },
              boxShadow: 1,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: '#C4A574',
              minWidth: 120,
              textAlign: 'center',
            }}
          >
            {currentYear}
          </Typography>

          <IconButton
            onClick={handleNextYear}
            sx={{
              bgcolor: '#C4A574',
              color: '#000',
              '&:hover': { bgcolor: '#B39562' },
              boxShadow: 1,
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>

        {/* Month Cards Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#C4A574' }} />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto', justifyContent: 'center' }}>
            {months.map((month, index) => {
              const monthData = monthsData[index + 1] || {};
              const workingDays = monthData.workingDays || 0;
              const expectedHours = monthData.expectedHours || 0;
              const holidays = monthData.holidays || 0;

              return (
                <Grid item key={month} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    onClick={() => handleMonthClick(index)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      boxShadow: '0 6px 18px rgba(0,0,0,0.6)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        borderColor: '#C4A574',
                        boxShadow: '0 12px 36px rgba(0,0,0,0.7)',
                        bgcolor: '#151515',
                      },
                      bgcolor: '#1f1f1f',
                      border: '1px solid rgba(196,165,116,0.08)',
                      p: 0,
                      justifyContent: 'center',
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 , justifyContent: 'center !important' }}>
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 44,
                          color: '#C4A574',
                          mb: 1,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#fff',
                          mb: 0.25,
                        }}
                      >
                        {month}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#9CA3AF',
                          mb: 2,
                        }}
                      >
                        {currentYear}
                      </Typography>

                      <Box
                        sx={{
                          bgcolor: '#2d2d2d',
                          borderRadius: 1,
                          p: 1.25,
                          mt: 1,
                          mx: 'auto',
                          width: '85%',
                          border: '1px solid rgba(196,165,116,0.04)',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: '#A5D6A7', fontWeight: 700 }}
                        >
                          {workingDays} Working Days
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}
                        >
                          {expectedHours} hours expected
                        </Typography>
                        {holidays > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#EF9A9A', display: 'block', mt: 0.5 }}
                          >
                            {holidays} holiday{holidays > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </AppLayout>
  );
}
